import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Mail, Phone, Building2, MapPin, User, ShoppingCart, DollarSign, UserCheck, Eye, FileText, Receipt, Trash2, CreditCard, Ban, ArrowUpRight, ArrowDownLeft, Shield, Search, Lock, Unlock, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tierColors = {
  retail: 'bg-zinc-700 text-zinc-300',
  trade: 'bg-blue-500/20 text-blue-400',
};

const PAYMENT_TERMS = [
  { value: 'net_7', label: 'Net 7 Days' },
  { value: 'net_14', label: 'Net 14 Days' },
  { value: 'net_30', label: 'Net 30 Days' },
  { value: 'net_60', label: 'Net 60 Days' },
  { value: 'cod', label: 'Cash on Delivery' },
];

const statusColors = {
  active: 'bg-green-500/20 text-green-400',
  suspended: 'bg-red-500/20 text-red-400',
  closed: 'bg-zinc-500/20 text-zinc-400',
};

export default function AdminCustomers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total: 0, manual: 0, registered: 0, admins: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = React.useRef(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [creditAccounts, setCreditAccounts] = useState({});
  const [creditAccount, setCreditAccount] = useState(null);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);

  const [form, setForm] = useState({
    name: '', company_name: '', email: '', phone: '', abn: '',
    billing_address: '', shipping_address: '', notes: '',
    account_type: 'cash', // cash or account
    payment_terms: 'cod', // cod, net_7, net_14, net_30, net_60
  });
  const [creditForm, setCreditForm] = useState({ credit_limit: '', payment_terms: 'net_30', notes: '' });
  const [paymentForm, setPaymentForm] = useState({ amount: '', reference: '', description: '' });
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPwForm, setResetPwForm] = useState({ newPassword: '' });

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { fetchCustomers(); fetchAllCreditAccounts(); }, []);

  // Handle search highlight - auto-open customer from global search
  useEffect(() => {
    const highlightId = searchParams.get('highlight') || location.state?.highlightId;
    if (!highlightId) return;

    const openCustomer = async (id) => {
      let customer = customers.find(c => c.id === id);
      if (!customer) {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/admin/customers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          customer = res.data;
        } catch { return; }
      }
      if (customer) {
        setSelectedCustomer(customer);
      }
      setSearchParams({}, { replace: true });
      window.history.replaceState({}, '');
    };

    openCustomer(highlightId);
  }, [customers, searchParams, location.state]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/all`, authHeaders);
      setCustomers(res.data.customers || []);
      setStats(res.data.stats || {});
    } catch (err) { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };

  const fetchAllCreditAccounts = async () => {
    try {
      const res = await axios.get(`${API}/admin/credit-accounts`, authHeaders);
      const map = {};
      (res.data.accounts || []).forEach(a => { map[a.customer_id] = a; });
      setCreditAccounts(map);
    } catch (err) { console.error('Failed to load credit accounts'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/admin/customers/${editingCustomer.id}`, form, authHeaders);
        toast.success('Customer updated');
      } else {
        await axios.post(`${API}/admin/customers`, form, authHeaders);
        toast.success('Customer created');
      }
      setShowModal(false); resetForm(); fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save customer'); }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({
      name: customer.name || '', company_name: customer.company_name || '',
      email: customer.email || '', phone: customer.phone || '',
      abn: customer.abn || '', billing_address: customer.billing_address || '',
      shipping_address: customer.shipping_address || '', notes: customer.notes || '',
      account_type: customer.account_type || 'cash',
      payment_terms: customer.payment_terms || 'cod',
    });
    setShowModal(true);
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete ${customer.name}? Their orders, invoices, and estimates will remain.`)) return;
    try {
      await axios.delete(`${API}/admin/customers/${customer.id}`, authHeaders);
      toast.success('Customer deleted'); fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const updateUserField = async (customerId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${customerId}`, updates, authHeaders);
      toast.success('Updated'); fetchCustomers();
    } catch (err) { toast.error('Failed to update'); }
  };

  const unlockUser = async (customerId, email) => {
    try {
      await axios.post(`${API}/admin/users/${customerId}/unlock`, {}, authHeaders);
      toast.success(`Account unlocked for ${email}`);
      fetchCustomers();
    } catch (err) { toast.error('Failed to unlock account'); }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setForm({ name: '', company_name: '', email: '', phone: '', abn: '', billing_address: '', shipping_address: '', notes: '', account_type: 'cash', payment_terms: 'cod' });
  };

  const filteredCustomers = customers.filter(c => {
    if (filter === 'registered' && c.customer_type !== 'registered') return false;
    if (filter === 'manual' && c.customer_type !== 'manual') return false;
    if (filter === 'admin' && !c.is_admin) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.company_name?.toLowerCase().includes(q));
    }
    return true;
  });

  const viewCustomerHistory = async (customer) => {
    setSelectedCustomer(customer); setHistoryLoading(true);
    setCreditAccount(null); setCreditTransactions([]);
    setShowHistoryModal(true);
    try {
      const histRes = await axios.get(`${API}/admin/customers/${customer.id}/history`, authHeaders);
      setCustomerHistory(histRes.data);
      const ca = creditAccounts[customer.id];
      if (ca) {
        setCreditLoading(true);
        try {
          const caRes = await axios.get(`${API}/admin/credit-accounts/${ca.id}`, authHeaders);
          setCreditAccount(caRes.data.account); setCreditTransactions(caRes.data.transactions || []);
        } catch (err) { console.error('Failed to load credit details'); }
        finally { setCreditLoading(false); }
      }
    } catch (err) { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  const handleCreateCreditAccount = async () => {
    if (!creditForm.credit_limit || !selectedCustomer) return;
    try {
      await axios.post(`${API}/admin/credit-accounts`, {
        customer_id: selectedCustomer.id, credit_limit: parseFloat(creditForm.credit_limit),
        payment_terms: creditForm.payment_terms, notes: creditForm.notes,
      }, authHeaders);
      toast.success('Credit account created'); setShowCreditModal(false);
      setCreditForm({ credit_limit: '', payment_terms: 'net_30', notes: '' });
      fetchAllCreditAccounts(); viewCustomerHistory(selectedCustomer);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const handleUpdateCreditAccount = async (updates) => {
    if (!creditAccount) return;
    try {
      await axios.put(`${API}/admin/credit-accounts/${creditAccount.id}`, updates, authHeaders);
      toast.success('Credit account updated'); fetchAllCreditAccounts(); viewCustomerHistory(selectedCustomer);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || !paymentForm.reference || !creditAccount) return;
    try {
      await axios.post(`${API}/admin/credit-accounts/${creditAccount.id}/payment`, {
        amount: parseFloat(paymentForm.amount), reference: paymentForm.reference, description: paymentForm.description,
      }, authHeaders);
      toast.success('Payment recorded'); setShowPaymentModal(false);
      setPaymentForm({ amount: '', reference: '', description: '' });
      fetchAllCreditAccounts(); viewCustomerHistory(selectedCustomer);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
  };

  const handleDeleteCreditAccount = async () => {
    if (!creditAccount) return;
    if (!window.confirm('Delete this credit account? Only possible if balance is zero.')) return;
    try {
      await axios.delete(`${API}/admin/credit-accounts/${creditAccount.id}`, authHeaders);
      toast.success('Credit account deleted'); setCreditAccount(null); setCreditTransactions([]);
      fetchAllCreditAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const fmtCur = (a) => `$${(a || 0).toFixed(2)}`;

  const handleAdminResetPassword = async () => {
    if (!resetPwForm.newPassword || resetPwForm.newPassword.length < 6 || !selectedCustomer) return;
    try {
      await axios.post(`${API}/admin/users/${selectedCustomer.id}/reset-password`, { new_password: resetPwForm.newPassword }, authHeaders);
      toast.success(`Password updated for ${selectedCustomer.email}`);
      setShowResetModal(false); setResetPwForm({ newPassword: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
  };

  const handleAdminSendResetLink = async (customer) => {
    if (!customer) return;
    try {
      const res = await axios.post(`${API}/admin/users/${customer.id}/send-reset-link`, {}, authHeaders);
      toast.success(res.data.message || `Reset link sent to ${customer.email}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send reset link'); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-customers">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">Customers & Users</h1>
            <p className="text-zinc-400 mt-1">Manage all accounts, roles, tiers, and credit</p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="add-customer-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Manual Customer
          </Button>
        </div>

        {/* Stats & Filters */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { key: 'all', label: 'Total', value: stats.total, color: 'text-white' },
            { key: 'registered', label: 'Registered', value: stats.registered, color: 'text-blue-400' },
            { key: 'manual', label: 'Manual', value: stats.manual, color: 'text-orange-400' },
            { key: 'admin', label: 'Admins', value: stats.admins, color: 'text-[#78BE20]' },
          ].map(s => (
            <div key={s.key} className={`bg-zinc-900 border rounded-sm p-4 cursor-pointer transition-all ${filter === s.key ? 'border-[#78BE20]' : 'border-zinc-800 hover:border-zinc-600'}`} onClick={() => setFilter(s.key)}>
              <p className="text-zinc-400 text-sm">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative" ref={searchRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 z-10" />
          <Input
            placeholder="Search by name, email, or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="customer-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white text-xs">Clear</button>
          )}
          {search.length > 0 && searchFocused && filteredCustomers.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl max-h-72 overflow-y-auto" data-testid="search-dropdown">
              <div className="px-3 py-1.5 text-[10px] uppercase text-zinc-500 border-b border-zinc-800">
                {filteredCustomers.length} result{filteredCustomers.length !== 1 ? 's' : ''}
              </div>
              {filteredCustomers.slice(0, 8).map(c => {
                const ca = creditAccounts[c.id];
                return (
                  <div
                    key={c.id}
                    className="px-3 py-2.5 hover:bg-zinc-800 cursor-pointer flex items-center justify-between border-b border-zinc-800/50 last:border-0"
                    onMouseDown={() => { setSearchFocused(false); setSearch(''); viewCustomerHistory(c); }}
                    data-testid={`search-result-${c.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold ${c.is_admin ? 'bg-[#78BE20]/20 text-[#78BE20]' : 'bg-zinc-800 text-zinc-400'}`}>
                        {c.is_admin ? <Shield className="h-3 w-3" /> : c.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{c.name}</p>
                        <p className="text-zinc-500 text-xs">{c.email}{c.company_name ? ` · ${c.company_name}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${tierColors[c.account_tier] || tierColors.retail}`}>{c.account_tier}</span>
                      {ca && <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusColors[ca.status]}`}><CreditCard className="h-2.5 w-2.5 inline mr-0.5" />{fmtCur(ca.available_credit)}</span>}
                    </div>
                  </div>
                );
              })}
              {filteredCustomers.length > 8 && (
                <div className="px-3 py-2 text-xs text-zinc-500 text-center">+ {filteredCustomers.length - 8} more results</div>
              )}
            </div>
          )}
        </div>

        {/* Customer Table */}
        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Tier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Orders</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Spent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Credit</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={8} className="px-4 py-4"><div className="h-8 bg-zinc-800 rounded animate-pulse" /></td></tr>
                ))
              ) : filteredCustomers.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500">No customers found</td></tr>
              ) : (
                filteredCustomers.map(customer => {
                  const ca = creditAccounts[customer.id];
                  return (
                    <tr key={customer.id} className="hover:bg-zinc-900/50" data-testid={`customer-row-${customer.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${customer.is_admin ? 'bg-[#78BE20]/20 text-[#78BE20]' : 'bg-zinc-800 text-zinc-400'}`}>
                            {customer.is_admin ? <Shield className="h-4 w-4" /> : customer.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{customer.name}</p>
                            <p className="text-zinc-500 text-xs">{customer.email}</p>
                            {customer.company_name && <p className="text-zinc-600 text-xs">{customer.company_name}</p>}
                          </div>
                          <div className="flex gap-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${customer.customer_type === 'registered' ? 'bg-blue-500/15 text-blue-400' : 'bg-orange-500/15 text-orange-400'}`}>
                              {customer.customer_type === 'registered' ? 'Reg' : 'Manual'}
                            </span>
                            {customer.trade_status === 'approved' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400">Trade</span>
                            )}
                            {customer.account_locked && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 flex items-center gap-0.5" data-testid={`locked-badge-${customer.id}`}>
                                <Lock className="h-2.5 w-2.5" /> Locked
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={customer.role || 'customer'} onValueChange={(v) => updateUserField(customer.id, { role: v })}>
                          <SelectTrigger className="w-28 h-8 text-xs bg-zinc-800 border-zinc-700" data-testid={`role-select-${customer.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="customer">Customer</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={customer.account_tier || 'retail'} onValueChange={(v) => updateUserField(customer.id, { account_tier: v })}>
                          <SelectTrigger className="w-28 h-8 text-xs bg-zinc-800 border-zinc-700" data-testid={`tier-select-${customer.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="retail">Retail</SelectItem>
                            <SelectItem value="trade">Trade</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={customer.account_type || 'cash'} onValueChange={(v) => updateUserField(customer.id, { account_type: v })}>
                          <SelectTrigger className="w-28 h-8 text-xs bg-zinc-800 border-zinc-700" data-testid={`type-select-${customer.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="account">Account</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-sm">{customer.orders_count || 0}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={customer.total_spent > 0 ? 'text-[#78BE20] font-medium' : 'text-zinc-500'}>{fmtCur(customer.total_spent)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {ca ? (
                          <span className={`text-xs px-2 py-1 rounded ${statusColors[ca.status]}`}>
                            {fmtCur(ca.available_credit)} avail
                          </span>
                        ) : (
                          <span className="text-zinc-600 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => viewCustomerHistory(customer)} className="h-8 px-2 text-zinc-400 hover:text-white" data-testid={`view-btn-${customer.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(customer)} className="h-8 px-2 text-zinc-400 hover:text-white">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {customer.account_locked && (
                            <Button variant="ghost" size="sm" onClick={() => unlockUser(customer.id, customer.email)} className="h-8 px-2 text-amber-400 hover:text-amber-300" data-testid={`unlock-btn-${customer.id}`} title="Unlock account">
                              <Unlock className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {!customer.is_admin && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(customer)} className="h-8 px-2 text-zinc-400 hover:text-red-400">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ─── Customer History Modal ─── */}
        <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-3">
                <User className="h-5 w-5" />
                {selectedCustomer?.name}
                {selectedCustomer?.company_name && <span className="text-zinc-400 font-normal text-sm">({selectedCustomer.company_name})</span>}
              </DialogTitle>
            </DialogHeader>
            {historyLoading ? (
              <div className="py-8 text-center text-zinc-400">Loading...</div>
            ) : customerHistory && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-zinc-800 border-zinc-700"><CardContent className="pt-4">
                    <p className="text-zinc-400 text-xs uppercase">Total Spent</p>
                    <p className="text-2xl font-bold text-[#78BE20]">{fmtCur(customerHistory.stats.total_spent)}</p>
                  </CardContent></Card>
                  <Card className="bg-zinc-800 border-zinc-700"><CardContent className="pt-4">
                    <p className="text-zinc-400 text-xs uppercase">Orders</p>
                    <p className="text-2xl font-bold text-white">{customerHistory.stats.total_orders}</p>
                  </CardContent></Card>
                  <Card className="bg-zinc-800 border-zinc-700"><CardContent className="pt-4">
                    <p className="text-zinc-400 text-xs uppercase">Invoices</p>
                    <p className="text-2xl font-bold text-white">{customerHistory.stats.total_invoices}</p>
                  </CardContent></Card>
                  <Card className="bg-zinc-800 border-zinc-700"><CardContent className="pt-4">
                    <p className="text-zinc-400 text-xs uppercase">Estimates</p>
                    <p className="text-2xl font-bold text-white">{customerHistory.stats.total_estimates}</p>
                  </CardContent></Card>
                </div>

                <div className="bg-zinc-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-semibold">Contact Information</h4>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleAdminSendResetLink(selectedCustomer)} className="text-blue-400 border-blue-500/30 h-7 text-xs" data-testid="send-reset-link-btn">
                        <Mail className="h-3 w-3 mr-1" /> Send Reset Link
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setResetPwForm({ newPassword: '' }); setShowResetModal(true); }} className="text-orange-400 border-orange-500/30 h-7 text-xs" data-testid="admin-reset-pw-btn">
                        <Lock className="h-3 w-3 mr-1" /> Set Password
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-zinc-300"><Mail className="h-4 w-4 text-zinc-500" /> {selectedCustomer?.email}</div>
                    {selectedCustomer?.phone && <div className="flex items-center gap-2 text-zinc-300"><Phone className="h-4 w-4 text-zinc-500" /> {selectedCustomer.phone}</div>}
                    {selectedCustomer?.billing_address && <div className="flex items-start gap-2 text-zinc-300 col-span-2"><MapPin className="h-4 w-4 text-zinc-500 mt-0.5" /> {selectedCustomer.billing_address}</div>}
                  </div>
                </div>

                <Tabs defaultValue="orders" className="w-full">
                  <TabsList className="bg-zinc-800">
                    <TabsTrigger value="orders" className="data-[state=active]:bg-zinc-700"><ShoppingCart className="h-4 w-4 mr-2" />Orders ({customerHistory.orders.length})</TabsTrigger>
                    <TabsTrigger value="invoices" className="data-[state=active]:bg-zinc-700"><Receipt className="h-4 w-4 mr-2" />Invoices ({customerHistory.invoices.length})</TabsTrigger>
                    <TabsTrigger value="estimates" className="data-[state=active]:bg-zinc-700"><FileText className="h-4 w-4 mr-2" />Estimates ({customerHistory.estimates.length})</TabsTrigger>
                    <TabsTrigger value="credit" className="data-[state=active]:bg-zinc-700" data-testid="credit-tab"><CreditCard className="h-4 w-4 mr-2" />Credit Account</TabsTrigger>
                  </TabsList>

                  <TabsContent value="orders" className="mt-4">
                    {customerHistory.orders.length === 0 ? <p className="text-zinc-400 text-center py-4">No orders</p> : (
                      <div className="space-y-2">{customerHistory.orders.map(o => (
                        <div key={o.id} className="bg-zinc-800 rounded-lg p-3 flex justify-between items-center">
                          <div><p className="text-white font-medium">Order #{o.order_number || o.id?.slice(0,8)}</p><p className="text-zinc-400 text-sm">{fmt(o.created_at)} - {o.items?.length || 0} items</p></div>
                          <div className="text-right"><p className="text-[#78BE20] font-bold">{fmtCur(o.total)}</p><Badge variant="secondary" className="text-xs">{o.status}</Badge></div>
                        </div>
                      ))}</div>
                    )}
                  </TabsContent>

                  <TabsContent value="invoices" className="mt-4">
                    {customerHistory.invoices.length === 0 ? <p className="text-zinc-400 text-center py-4">No invoices</p> : (
                      <div className="space-y-2">{customerHistory.invoices.map(i => (
                        <div key={i.id} className="bg-zinc-800 rounded-lg p-3 flex justify-between items-center">
                          <div><p className="text-white font-medium">Invoice #{i.invoice_number}</p><p className="text-zinc-400 text-sm">{fmt(i.created_at)}</p></div>
                          <div className="text-right"><p className="text-[#78BE20] font-bold">{fmtCur(i.total)}</p><Badge variant="secondary" className="text-xs">{i.payment_status}</Badge></div>
                        </div>
                      ))}</div>
                    )}
                  </TabsContent>

                  <TabsContent value="estimates" className="mt-4">
                    {customerHistory.estimates.length === 0 ? <p className="text-zinc-400 text-center py-4">No estimates</p> : (
                      <div className="space-y-2">{customerHistory.estimates.map(e => (
                        <div key={e.id} className="bg-zinc-800 rounded-lg p-3 flex justify-between items-center">
                          <div><p className="text-white font-medium">Estimate #{e.estimate_number}</p><p className="text-zinc-400 text-sm">{fmt(e.created_at)}</p></div>
                          <div className="text-right"><p className="text-[#78BE20] font-bold">{fmtCur(e.total)}</p><Badge variant="secondary" className="text-xs">{e.status}</Badge></div>
                        </div>
                      ))}</div>
                    )}
                  </TabsContent>

                  <TabsContent value="credit" className="mt-4" data-testid="credit-tab-content">
                    {creditLoading ? <div className="text-zinc-400 text-center py-6">Loading...</div> : creditAccount ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-4 gap-3 p-4 bg-zinc-800/50 rounded-lg">
                          <div><p className="text-zinc-500 text-xs uppercase">Credit Limit</p><p className="text-xl font-bold text-white">{fmtCur(creditAccount.credit_limit)}</p></div>
                          <div><p className="text-zinc-500 text-xs uppercase">Balance Owed</p><p className="text-xl font-bold text-orange-400">{fmtCur(creditAccount.current_balance)}</p></div>
                          <div><p className="text-zinc-500 text-xs uppercase">Available</p><p className="text-xl font-bold text-green-400">{fmtCur(creditAccount.available_credit)}</p></div>
                          <div><p className="text-zinc-500 text-xs uppercase">Status</p><Badge className={`mt-1 ${statusColors[creditAccount.status]}`}>{creditAccount.status}</Badge></div>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                          <div className="text-sm text-zinc-400">
                            Terms: <span className="text-white">{creditAccount.payment_terms?.replace('_', ' ').toUpperCase()}</span>
                            {creditAccount.notes && <span className="ml-4 text-zinc-300">{creditAccount.notes}</span>}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setShowPaymentModal(true)} className="text-green-400 border-green-500/30" data-testid="record-payment-btn"><DollarSign className="h-3 w-3 mr-1" />Payment</Button>
                            {creditAccount.status === 'active' ? (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateCreditAccount({ status: 'suspended' })} className="text-red-400 border-red-500/30"><Ban className="h-3 w-3 mr-1" />Suspend</Button>
                            ) : creditAccount.status === 'suspended' ? (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateCreditAccount({ status: 'active' })} className="text-green-400 border-green-500/30">Activate</Button>
                            ) : null}
                            <Button size="sm" variant="outline" onClick={() => { setCreditForm({ credit_limit: creditAccount.credit_limit?.toString() || '', payment_terms: creditAccount.payment_terms || 'net_30', notes: creditAccount.notes || '' }); setShowCreditModal(true); }} data-testid="edit-credit-btn"><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                            <Button size="sm" variant="outline" onClick={handleDeleteCreditAccount} className="text-red-400 border-red-500/30"><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-2 text-sm">Transaction History</h4>
                          {creditTransactions.length === 0 ? <p className="text-zinc-400 text-sm text-center py-4">No transactions yet</p> : (
                            <div className="space-y-2 max-h-60 overflow-auto">{creditTransactions.map(t => (
                              <div key={t.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded">
                                <div className="flex items-center gap-3">
                                  {t.type === 'payment' ? <ArrowDownLeft className="w-4 h-4 text-green-400" /> : <ArrowUpRight className="w-4 h-4 text-orange-400" />}
                                  <div><p className="text-sm font-medium text-white">{t.description}</p><p className="text-xs text-zinc-500">{fmt(t.created_at)} {t.reference && `- ${t.reference}`}</p></div>
                                </div>
                                <div className="text-right">
                                  <p className={`font-mono text-sm ${t.amount < 0 ? 'text-green-400' : 'text-orange-400'}`}>{t.amount < 0 ? '-' : '+'}${Math.abs(t.amount).toFixed(2)}</p>
                                  <p className="text-xs text-zinc-500">Bal: {fmtCur(t.balance_after)}</p>
                                </div>
                              </div>
                            ))}</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CreditCard className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
                        <p className="text-zinc-400 mb-4">No credit account for this customer</p>
                        <Button onClick={() => { setCreditForm({ credit_limit: '', payment_terms: 'net_30', notes: '' }); setShowCreditModal(true); }} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="create-credit-account-btn">
                          <Plus className="h-4 w-4 mr-2" /> Create Credit Account
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ─── Credit Account Modal ─── */}
        <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader><DialogTitle className="text-white">{creditAccount ? 'Edit' : 'Create'} Credit Account</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Customer</Label><p className="text-white font-medium mt-1">{selectedCustomer?.name} ({selectedCustomer?.email})</p></div>
              <div><Label>Credit Limit *</Label><Input type="number" value={creditForm.credit_limit} onChange={(e) => setCreditForm({ ...creditForm, credit_limit: e.target.value })} placeholder="5000" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="credit-limit-input" /></div>
              <div><Label>Payment Terms</Label>
                <Select value={creditForm.payment_terms} onValueChange={(v) => setCreditForm({ ...creditForm, payment_terms: v })}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">{PAYMENT_TERMS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea value={creditForm.notes} onChange={(e) => setCreditForm({ ...creditForm, notes: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="Optional notes..." /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreditModal(false)}>Cancel</Button>
              <Button onClick={() => { if (creditAccount) { handleUpdateCreditAccount({ credit_limit: parseFloat(creditForm.credit_limit), payment_terms: creditForm.payment_terms, notes: creditForm.notes }); setShowCreditModal(false); } else { handleCreateCreditAccount(); } }} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="save-credit-btn">{creditAccount ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Record Payment Modal ─── */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader><DialogTitle className="text-white">Record Payment</DialogTitle></DialogHeader>
            {creditAccount && (
              <div className="space-y-4">
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-sm text-zinc-400">Recording payment for</p>
                  <p className="font-medium text-white">{selectedCustomer?.name}</p>
                  <p className="text-lg text-orange-400 mt-2">Current Balance: {fmtCur(creditAccount.current_balance)}</p>
                </div>
                <div><Label>Amount *</Label><Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} placeholder="0.00" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="payment-amount-input" /></div>
                <div><Label>Reference *</Label><Input value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} placeholder="e.g., Bank transfer 12345" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="payment-reference-input" /></div>
                <div><Label>Description</Label><Input value={paymentForm.description} onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })} placeholder="Optional" className="mt-1 bg-zinc-800 border-zinc-700" /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
              <Button onClick={handleRecordPayment} className="bg-green-600 hover:bg-green-700" data-testid="submit-payment-btn">Record Payment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Admin Set Password Modal ─── */}
        <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader><DialogTitle className="text-white">Set Password for {selectedCustomer?.name}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm">This will immediately change the customer's password. They will need to use the new password to log in.</p>
              <div>
                <Label>New Password *</Label>
                <Input type="password" value={resetPwForm.newPassword} onChange={(e) => setResetPwForm({ newPassword: e.target.value })} placeholder="Min 6 characters" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="admin-new-password-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetModal(false)}>Cancel</Button>
              <Button onClick={handleAdminResetPassword} disabled={resetPwForm.newPassword.length < 6} className="bg-orange-600 hover:bg-orange-700" data-testid="admin-set-password-btn">Set Password</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Add/Edit Customer Modal ─── */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl">
            <DialogHeader><DialogTitle className="text-white">{editingCustomer ? 'Edit Customer' : 'Add Manual Customer'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Contact Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" required /></div>
                <div><Label>Company Name</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" required /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
              </div>
              <div><Label>ABN</Label><Input value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="XX XXX XXX XXX" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Account Type</Label>
                  <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="form-account-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="account">Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Payment Terms</Label>
                  <Select value={form.payment_terms} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="form-payment-terms"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">{PAYMENT_TERMS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Billing Address</Label><Textarea value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" rows={2} /></div>
              <div><Label>Shipping Address</Label><Textarea value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" rows={2} placeholder="Leave blank if same as billing" /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" rows={2} /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#78BE20] text-black hover:bg-[#65a31b]">{editingCustomer ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
